import ReactPaginate from 'react-paginate';
import Tooltip from '@components/primitives/tooltip/Tooltip';
import Button from '@components/primitives/button/Button';
import { useTranslation } from 'react-i18next';
import { Table } from '@tanstack/table-core';
import ArrowIcon from '@images/svg/arrow.svg?react';
import CrossIcon from '@images/svg/close.svg?react';
import { ReactNode } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { DateRangeFilterValue } from '@components/primitives/dataGrid/components/filters/DateRangeFilter';
import { format } from 'date-fns';
import { getCurrentLocale } from '@utils/locales';
import { NewNumericFilterValue, NumericFilterValue } from '@components/primitives/dataGrid/components/filters/NumericFilter';
import { ColumnFiltersState, ColumnFilterValue } from '@components/primitives/dataGrid/DataGrid';
import DataGridFooterTotals from './components/footer/DataGridFooterTotals';

interface Props<TData> extends DataGridFooterOptionalProps {
    data: TData[];
    allColumns: Array<ColumnDef<TData>>;
    table: Table<TData>;
    renderNrOfRows?: (nrOfRows: number, nrOfFilteredRows: number, selectedIndex: number) => ReactNode;
    purifiedColumnFilters: ColumnFiltersState;
    purifiedSorting: SortingState;
    resetColumnFilters: () => void;
    resetSorting: () => void;
    selectedRow?: string;
}

export type DataGridFooterOptionalProps = {
    showTotals?: boolean;
};

const DataGridFooter = <TData extends object>({
    table,
    renderNrOfRows,
    purifiedColumnFilters,
    purifiedSorting,
    resetColumnFilters,
    resetSorting,
    data,
    allColumns,
    showTotals = false,
    selectedRow,
}: Props<TData>) => {
    const { t } = useTranslation();
    const currentLocale = getCurrentLocale();

    const paginationActivated = table.getPageCount() > 1;
    const currentPage = table.getState().pagination.pageIndex;

    function isNumericFilterValue(filterValue: ColumnFilterValue): filterValue is NewNumericFilterValue {
        return (filterValue as NewNumericFilterValue)?.isRangeMode !== undefined;
    }

    const columnFilters = purifiedColumnFilters?.filter((filter) => {
        if (isNumericFilterValue(filter.value)) {
            return !!filter.value?.min || !!filter.value?.max;
        }

        return true;
    });

    const onResetFilters = () => {
        resetColumnFilters();
    };

    const onResetSorting = () => {
        resetSorting();
    };

    if (!paginationActivated && !columnFilters?.length && !renderNrOfRows && !purifiedSorting?.length) {
        return null;
    }

    const selectedIndex = table.getPrePaginationRowModel().rows.findIndex((row) => row.id === selectedRow);

    if (!allColumns.length) {
        return null;
    }

    return (
        <div className="dataGrid__footer">
            <div className="dataGrid__footerInner">
                {showTotals && <DataGridFooterTotals table={table} />}

                <div className="dataGrid__footerPagination">
                    {paginationActivated ? (
                        <ReactPaginate
                            forcePage={currentPage}
                            className="pagination"
                            breakLabel="..."
                            nextLabel={<ArrowIcon />}
                            onPageChange={({ selected }) => {
                                table.setPageIndex(selected);
                            }}
                            pageRangeDisplayed={3}
                            marginPagesDisplayed={1}
                            pageCount={table.getPageCount()}
                            previousLabel={<ArrowIcon />}
                        />
                    ) : null}

                    {columnFilters?.length ? (
                        <div className="dataGrid__footerButtons">
                            <Tooltip
                                title={t('resetFilters.tooltip.title', { ns: 'dataGrid' })}
                                list={columnFilters.map((filter) => {
                                    const filterColumn = allColumns.find((column) => column.id === filter.id);

                                    if (filterColumn?.filterFn === 'dateRange') {
                                        const range = filter.value as DateRangeFilterValue;
                                        const [startTimeStamp, endTimeStamp] = range ?? [];
                                        const startDate = startTimeStamp ? new Date(startTimeStamp) : null;
                                        const endDate = endTimeStamp ? new Date(endTimeStamp) : null;
                                        const columnTitle = filterColumn.header ? filterColumn.header : filter.id;

                                        if (startDate && !endDate) {
                                            return `${columnTitle}: > ${format(new Date(startDate), currentLocale.dateFormat)}`;
                                        }

                                        if (!startDate && endDate) {
                                            return `${columnTitle}: < ${format(new Date(endDate), currentLocale.dateFormat)}`;
                                        }

                                        if (startDate && endDate) {
                                            return `${columnTitle}: ${format(new Date(startDate), currentLocale.dateFormat)} - ${format(new Date(endDate), currentLocale.dateFormat)}`;
                                        }
                                    }

                                    if (filterColumn?.filterFn === 'numeric') {
                                        let numericFilterValue = filter.value as NewNumericFilterValue;

                                        // @ts-expect-error Migrate old filter values to new format
                                        if (numericFilterValue?.length) {
                                            const oldColumnFilterValue = filter.value as NumericFilterValue;

                                            if (oldColumnFilterValue) {
                                                numericFilterValue = {
                                                    min: Number(oldColumnFilterValue[0]),
                                                    max: Number(oldColumnFilterValue[1]),
                                                    isRangeMode: oldColumnFilterValue[0] !== oldColumnFilterValue[1],
                                                };
                                            }
                                        }

                                        const { min, max } = numericFilterValue ?? {};
                                        const columnTitle = filterColumn.header ? filterColumn.header : filter.id;

                                        if (min && !max) {
                                            return `${columnTitle}: > ${min}`;
                                        }

                                        if (!min && max) {
                                            return `${columnTitle}: < ${max}`;
                                        }

                                        if (min && max) {
                                            if (+min === +max) {
                                                return `${columnTitle}: ${min}`;
                                            }

                                            return `${columnTitle}: ${min} - ${max}`;
                                        }
                                    }

                                    return filterColumn ? `${filterColumn.header}: ${filter.value}` : filter.id;
                                })}
                            >
                                <Button primary onClick={onResetFilters} icon={<CrossIcon />} testId="list__reset-filters">
                                    {t('resetFilters.button', { ns: 'dataGrid' })}
                                </Button>
                            </Tooltip>
                        </div>
                    ) : null}

                    {purifiedSorting?.length ? (
                        <div className="dataGrid__footerButtons">
                            <Tooltip
                                title={t('resetSorting.tooltip.title', { ns: 'dataGrid' })}
                                list={purifiedSorting.map((sorting) => {
                                    const sortingColumn = allColumns.find((column) => column.id === sorting.id);
                                    return sortingColumn ? (sortingColumn.header as string) : sorting.id;
                                })}
                            >
                                <Button primary onClick={onResetSorting} icon={<CrossIcon />}>
                                    {t('resetSorting.button', { ns: 'dataGrid' })}
                                </Button>
                            </Tooltip>
                        </div>
                    ) : null}

                    {renderNrOfRows ? <div className="dataGrid__footerInfo">{renderNrOfRows(data.length, table.getPrePaginationRowModel().rows.length, selectedIndex)}</div> : null}
                </div>
            </div>
        </div>
    );
};

export default DataGridFooter;
